/*Bearer ${token}`
 * Ad01FilingWizard
 *
 * In-app AD01 (Registered Office Address Change) filing wizard.
 *
 * Steps:
 *  0 — Review current address
 *  1 — Enter new address
 *  2 — Confirm (old vs new)
 *  3 — Filing progress
 *  4 — Receipt
 */

import { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { createAd01Filing, pollAd01Status, listAd01Filings } from "services/tenantUserAdminService";
import { TC, FS, FW } from "../../tokens/text";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { label: "Current Address", icon: HomeWorkIcon },
  { label: "New Address",     icon: EditLocationIcon },
  { label: "Confirm",         icon: FactCheckIcon },
  { label: "Filing",          icon: CloudUploadIcon },
  { label: "Receipt",         icon: TaskAltIcon },
];

const FILING_STAGES = [
  { key: "open_txn",      label: "Opening filing transaction…" },
  { key: "create_res",    label: "Registering address change resource…" },
  { key: "submit",        label: "Submitting to Companies House…" },
  { key: "done",          label: "Submission complete" },
];

const EMPTY_ADDRESS = {
  addressLine1: "",
  addressLine2: "",
  locality:     "",
  region:       "",
  postalCode:   "",
  country:      "United Kingdom",
};

const CARD_SX = {
  borderRadius: "12px",
  border: "1px solid #d8e6f7",
  background: "#f8fbff",
  p: 2,
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.88rem", background: "#fff",
    "& fieldset": { borderColor: "rgba(0,87,216,0.2)" },
    "&:hover fieldset": { borderColor: "rgba(0,87,216,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#0057d8" },
  },
  "& .MuiInputLabel-root": { color: TC.tertiary, fontSize: "0.78rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#4da6ff" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtAddress(a) {
  if (!a) return "—";
  if (typeof a === "string") return a;
  return [a.addressLine1, a.addressLine2, a.locality, a.region, a.postalCode, a.country]
    .filter(Boolean).join(", ");
}

function AddressField({ label, value, onChange, required, multiline }) {
  return (
    <TextField
      fullWidth size="small" label={label}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      multiline={multiline}
      rows={multiline ? 2 : 1}
      InputLabelProps={{ shrink: true }}
      sx={{ ...INPUT_SX, mb: 1.5 }}
    />
  );
}

function AddressCard({ title, address, accentColor = "#0057d8" }) {
  return (
    <VuiBox p={2} sx={{
      borderRadius: "10px",
      background: "#f8fbff",
      border: `1px solid ${accentColor}33`,
      borderLeft: `3px solid ${accentColor}`,
    }}>
      <VuiTypography variant="caption" sx={{ fontSize: FS.xs, fontWeight: FW.bold, color: accentColor, textTransform: "uppercase", letterSpacing: "0.07em", display: "block", mb: 1 }}>
        {title}
      </VuiTypography>
      {[
        ["Line 1",     address?.addressLine1],
        ["Line 2",     address?.addressLine2],
        ["Town/City",  address?.locality],
        ["Region",     address?.region],
        ["Postcode",   address?.postalCode],
        ["Country",    address?.country],
      ].filter(([, v]) => v).map(([lbl, val]) => (
        <VuiBox key={lbl} display="flex" gap={1} mb={0.4}>
          <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: TC.tertiary, minWidth: 60 }}>{lbl}</VuiTypography>
          <VuiTypography variant="caption" sx={{ fontSize: FS.sm, color: TC.primary, fontWeight: FW.semibold }}>{val}</VuiTypography>
        </VuiBox>
      ))}
    </VuiBox>
  );
}

// ─── Step components ──────────────────────────────────────────────────────────

function StepReview({ profile }) {
  return (
    <VuiBox>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.md, display: "block", mb: 2 }}>
        Review the current registered address before entering the new one.
      </VuiTypography>
      <AddressCard
        title="Current Registered Address"
        address={profile?.registeredAddressObj || { addressLine1: profile?.registeredAddress }}
        accentColor="#0057d8"
      />
      {profile?.companyRegNumber && (
        <VuiBox mt={1.5} display="flex" gap={1} alignItems="center">
          <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.sm }}>Company Number:</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.sm }}>{profile.companyRegNumber}</VuiTypography>
        </VuiBox>
      )}
    </VuiBox>
  );
}

function StepNewAddress({ address, onChange }) {
  const set = (field) => (val) => onChange({ ...address, [field]: val });
  return (
    <VuiBox>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.md, display: "block", mb: 2 }}>
        Enter the full new registered address. This will be submitted directly to Companies House.
      </VuiTypography>
      <AddressField label="Address Line 1 *" value={address.addressLine1} onChange={set("addressLine1")} required />
      <AddressField label="Address Line 2"   value={address.addressLine2} onChange={set("addressLine2")} />
      <AddressField label="Town / City *"    value={address.locality}     onChange={set("locality")}     required />
      <AddressField label="County / Region"  value={address.region}       onChange={set("region")} />
      <AddressField label="Postcode *"       value={address.postalCode}   onChange={set("postalCode")}   required />
      <AddressField label="Country"          value={address.country}      onChange={set("country")} />
    </VuiBox>
  );
}

function StepConfirm({ profile, newAddress, notes, onNotes }) {
  return (
    <VuiBox>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.md, display: "block", mb: 2 }}>
        Review the change before filing. Once submitted this cannot be undone.
      </VuiTypography>
      <VuiBox display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
        <VuiBox flex={1}>
          <AddressCard
            title="Current Address"
            address={profile?.registeredAddressObj || { addressLine1: profile?.registeredAddress }}
            accentColor="#9e9e9e"
          />
        </VuiBox>
        <VuiBox display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
          <ArrowForwardIcon sx={{ color: TC.link, fontSize: "1.5rem" }} />
        </VuiBox>
        <VuiBox flex={1}>
          <AddressCard title="New Address" address={newAddress} accentColor="#2e7d32" />
        </VuiBox>
      </VuiBox>
      <VuiBox mt={2}>
        <TextField
          fullWidth multiline rows={2} size="small"
          label="Internal notes (optional)"
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={INPUT_SX}
          placeholder="e.g. Client requested change effective 1 Jun 2025"
        />
      </VuiBox>
    </VuiBox>
  );
}

function StepFiling({ stage, error }) {
  const current = FILING_STAGES.findIndex((s) => s.key === stage);
  return (
    <VuiBox>
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.md, display: "block", mb: 3 }}>
        {error ? "Filing encountered an error." : stage === "done" ? "Filing submitted successfully." : "Filing with Companies House…"}
      </VuiTypography>

      {FILING_STAGES.map((s, i) => {
        const done    = i < current || stage === "done";
        const active  = i === current && !error && stage !== "done";
        const errored = !!error && i === current;
        return (
          <VuiBox key={s.key} display="flex" alignItems="center" gap={1.5} mb={1.8}>
            <VuiBox sx={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: errored ? "rgba(211,47,47,0.1)" : done ? "rgba(46,125,50,0.1)" : active ? "rgba(0,87,216,0.1)" : "#f4f8ff",
              border: `1px solid ${errored ? "#c62828" : done ? "#2e7d32" : active ? "#0057d8" : "#d8e6f7"}`,
            }}>
              {errored   ? <ErrorOutlineIcon sx={{ fontSize: "1rem", color: "#c62828" }} />
               : done    ? <CheckCircleIcon  sx={{ fontSize: "1rem", color: "#2e7d32" }} />
               : active  ? <CircularProgress size={14} sx={{ color: TC.link }} />
               :            <VuiTypography variant="caption" sx={{ fontSize: "0.65rem", color: TC.tertiary, fontWeight: FW.bold }}>{i + 1}</VuiTypography>
              }
            </VuiBox>
            <VuiBox flex={1}>
              <VuiTypography variant="caption" sx={{
                fontSize: FS.md,
                fontWeight: active || done ? FW.semibold : FW.medium,
                color: errored ? "#c62828" : done ? "#2e7d32" : active ? TC.primary : TC.tertiary,
              }}>
                {s.label}
              </VuiTypography>
              {errored && error && (
                <VuiTypography variant="caption" sx={{ fontSize: FS.xs, color: TC.error, display: "block", mt: 0.3 }}>
                  {error}
                </VuiTypography>
              )}
            </VuiBox>
          </VuiBox>
        );
      })}

      {!error && stage !== "done" && (
        <LinearProgress sx={{ mt: 1, borderRadius: 4, "& .MuiLinearProgress-bar": { background: "#0057d8" } }} />
      )}
    </VuiBox>
  );
}

function StepReceipt({ filing, onPoll, polling, pollingError, onClose }) {
  const isFiled    = filing?.status === "filed";
  const isPending  = filing?.status === "filing_submitted";
  const isRejected = filing?.status === "rejected";
  const isError    = filing?.status === "error";

  return (
    <VuiBox>
      {isFiled && (
        <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(46,125,50,0.07)", border: "1px solid rgba(46,125,50,0.3)", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <TaskAltIcon sx={{ color: "#2e7d32", mt: "2px" }} />
          <VuiBox>
            <VuiTypography sx={{ color: "#2e7d32", fontWeight: FW.bold, fontSize: FS.base }}>AD01 Filed Successfully</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              The address change has been accepted by Companies House.
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}
      {isPending && (
        <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(0,87,216,0.06)", border: "1px solid rgba(0,87,216,0.25)", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <CircularProgress size={18} sx={{ color: TC.link, mt: "2px", flexShrink: 0 }} />
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base }}>Processing at Companies House</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              The filing is queued. Use the poll button to check for updates.
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}
      {(isRejected || isError) && (
        <VuiBox mb={2} p={2} sx={{ borderRadius: "10px", background: "rgba(211,47,47,0.07)", border: "1px solid rgba(211,47,47,0.3)", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <ErrorOutlineIcon sx={{ color: "#c62828", mt: "2px" }} />
          <VuiBox>
            <VuiTypography sx={{ color: "#c62828", fontWeight: FW.bold, fontSize: FS.base }}>Filing {isRejected ? "Rejected" : "Error"}</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
              {filing?.filingLastError || "CH rejected this filing. Please check the address and try again."}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      )}

      {/* Detail rows */}
      {[
        ["Transaction ID",   filing?.transactionId],
        ["Filing Reference", filing?.filingReference],
        ["Status",           filing?.status],
        ["Filed At",         filing?.filedAt ? new Date(filing.filedAt).toLocaleString("en-GB") : null],
        ["New Address",      filing?.newAddress ? fmtAddress(filing.newAddress) : null],
      ].filter(([, v]) => v).map(([lbl, val]) => (
        <VuiBox key={lbl} display="flex" gap={1} mb={0.8} flexWrap="wrap">
          <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: FS.sm, minWidth: 110 }}>{lbl}</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.primary, fontWeight: FW.semibold, fontSize: FS.sm, wordBreak: "break-all" }}>{val}</VuiTypography>
        </VuiBox>
      ))}

      {isPending && (
        <VuiBox mt={2} display="flex" alignItems="center" gap={1.5}>
          <VuiButton size="small" color="info" variant="outlined"
            onClick={onPoll} disabled={polling}
            startIcon={polling ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            {polling ? "Checking…" : "Check Status"}
          </VuiButton>
          {pollingError && (
            <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: FS.sm }}>{pollingError}</VuiTypography>
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function Ad01FilingWizard({ open, onClose, tenantId, clientId, profile, onFiled }) {
  const [step,         setStep]         = useState(0);
  const [newAddress,   setNewAddress]   = useState({ ...EMPTY_ADDRESS });
  const [notes,        setNotes]        = useState("");
  const [filingStage,  setFilingStage]  = useState("open_txn");
  const [filingError,  setFilingError]  = useState("");
  const [filing,       setFiling]       = useState(null);
  const [polling,      setPolling]      = useState(false);
  const [pollingError, setPollingError] = useState("");

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setStep(0);
      setNewAddress({ ...EMPTY_ADDRESS, country: "United Kingdom" });
      setNotes("");
      setFilingStage("open_txn");
      setFilingError("");
      setFiling(null);
      setPolling(false);
      setPollingError("");
    }
  }, [open]);

  const newAddressValid =
    newAddress.addressLine1.trim() &&
    newAddress.locality.trim() &&
    newAddress.postalCode.trim();

  const handleFile = useCallback(async () => {
    setStep(3);
    setFilingStage("open_txn");
    setFilingError("");

    // Simulate stage progression for UX — real stages happen server-side
    const stageTimer = setTimeout(() => setFilingStage("create_res"), 800);
    const stageTimer2 = setTimeout(() => setFilingStage("submit"), 1800);

    try {
      const result = await createAd01Filing(tenantId, clientId, { newAddress, notes });
      clearTimeout(stageTimer);
      clearTimeout(stageTimer2);
      setFilingStage("done");
      setFiling(result);
      setTimeout(() => setStep(4), 600);
      onFiled?.();
    } catch (err) {
      clearTimeout(stageTimer);
      clearTimeout(stageTimer2);
      setFilingError(err?.message || "Filing failed. Check your CH credentials and try again.");
    }
  }, [tenantId, clientId, newAddress, notes, onFiled]);

  const handlePoll = useCallback(async () => {
    if (!filing?.id) return;
    setPolling(true); setPollingError("");
    try {
      const updated = await pollAd01Status(tenantId, clientId, filing.id);
      setFiling((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      setPollingError(err?.message || "Status check failed.");
    } finally { setPolling(false); }
  }, [tenantId, clientId, filing]);

  // Step content
  const stepContent = [
    <StepReview key="review" profile={profile} />,
    <StepNewAddress key="new" address={newAddress} onChange={setNewAddress} />,
    <StepConfirm key="confirm" profile={profile} newAddress={newAddress} notes={notes} onNotes={setNotes} />,
    <StepFiling key="filing" stage={filingStage} error={filingError} />,
    <StepReceipt key="receipt" filing={filing} onPoll={handlePoll} polling={polling} pollingError={pollingError} onClose={onClose} />,
  ];

  const canNext = [
    true,
    !!newAddressValid,
    true,
    false,
    false,
  ];

  const isFilingStep   = step === 3;
  const isReceiptStep  = step === 4;
  const isFilingActive = isFilingStep && !filingError && filingStage !== "done";

  return (
    <Dialog open={open} onClose={isFilingActive ? undefined : onClose}
      aria-labelledby="ad01-filing-dialog-title"
      aria-describedby="ad01-filing-dialog-description"
      maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", border: "1px solid #d8e6f7", background: "#fff", overflow: "hidden" } }}>

      {/* Title bar */}
      <DialogTitle id="ad01-filing-dialog-title" sx={{
        px: 3, py: 1.8,
        background: "linear-gradient(135deg, #0a1f4e 0%, #0d2860 100%)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid rgba(0,117,255,0.2)",
      }}>
        <VuiBox display="flex" alignItems="center" gap={1.2}>
          <EditLocationIcon aria-hidden="true" sx={{ color: "#4da6ff", fontSize: "1.15rem" }} />
          <VuiTypography sx={{ color: "#fff", fontWeight: FW.bold, fontSize: FS.base }}>
            AD01 — Registered Office Address Change
          </VuiTypography>
        </VuiBox>
        {!isFilingActive && (
          <IconButton size="small" aria-label="Close AD01 filing dialog" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#fff" } }}>
            <CloseIcon aria-hidden="true" sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </DialogTitle>

      {/* Stepper */}
      <VuiBox px={3} py={1.5} sx={{ background: "#f4f8ff", borderBottom: "1px solid #e4eef8" }}>
        <Stepper activeStep={step} alternativeLabel
          sx={{
            "& .MuiStepLabel-label":           { fontSize: "0.62rem", color: TC.tertiary, mt: "2px !important" },
            "& .MuiStepLabel-label.Mui-active": { color: TC.primary, fontWeight: FW.bold },
            "& .MuiStepLabel-label.Mui-completed": { color: "#2e7d32" },
            "& .MuiStepIcon-root":             { color: "#d8e6f7",  fontSize: "1.1rem" },
            "& .MuiStepIcon-root.Mui-active":  { color: TC.link },
            "& .MuiStepIcon-root.Mui-completed": { color: "#2e7d32" },
            "& .MuiStepConnector-line":        { borderColor: "#d8e6f7" },
          }}>
          {STEPS.map((s) => (
            <Step key={s.label}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </VuiBox>

      {/* Body */}
      <DialogContent id="ad01-filing-dialog-description" sx={{ px: 3, py: 2.5, minHeight: 260 }}>
        {stepContent[step]}
      </DialogContent>

      {/* Actions */}
      {!isFilingStep && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, justifyContent: "space-between" }}>
          <VuiButton size="small" variant="outlined" color="secondary"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
            startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm, color: TC.secondary, borderColor: "#d8e6f7", "&:hover": { borderColor: TC.secondary } }}>
            Back
          </VuiButton>

          {isReceiptStep ? (
            <VuiButton size="small" color="info" variant="contained"
              onClick={onClose}
              sx={{ fontSize: FS.sm }}>
              Close
            </VuiButton>
          ) : step === 2 ? (
            <VuiButton size="small" color="info" variant="contained"
              onClick={handleFile}
              disabled={!newAddressValid}
              startIcon={<CloudUploadIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                fontSize: FS.sm,
                background: "linear-gradient(135deg,#1b5e20,#2e7d32)",
                "&:hover:not(:disabled)": { background: "linear-gradient(135deg,#145214,#1b5e20)" },
              }}>
              File AD01 with Companies House
            </VuiButton>
          ) : (
            <VuiButton size="small" color="info" variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext[step]}
              endIcon={<ArrowForwardIcon sx={{ fontSize: "13px !important" }} />}
              sx={{ fontSize: FS.sm }}>
              Next
            </VuiButton>
          )}
        </DialogActions>
      )}

      {/* Filing step retry button */}
      {isFilingStep && filingError && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, justifyContent: "space-between" }}>
          <VuiButton size="small" variant="outlined" color="secondary"
            onClick={() => setStep(2)}
            startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            Back
          </VuiButton>
          <VuiButton size="small" color="info" variant="contained"
            onClick={handleFile}
            startIcon={<CloudUploadIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            Retry
          </VuiButton>
        </DialogActions>
      )}
    </Dialog>
  );
}


